'use client'

import { create } from 'zustand'

import { UpdateCategoriesFn } from '@hooks/search/use-update-categories'
import { DynamicSearchInput } from '@libs/graphql/types'
import { categoriesConstants } from '@page/search/categories/categories.constants'
import { DynamicFilters } from '@page/search/categories/filters/types'
import { defaultSearchFilters } from '@page/search/search.default-search-filters'
import {
  SearchResultSpaceType,
  SearchResultUseCase,
} from '@page/search/search.graphql'
import { DynamicDefaults, SearchFilters } from '@page/search/search.types'
import { SearchType } from '@services/Analytics/tracking-plan/segment'
import { IMPERIAL_SYSTEM } from '@services/localization/localization.constants'
import { Address } from '@services/maps/types'

export type PerformDynamicSearchOptions = {
  filters?: Partial<DynamicSearchInput>
  searchType?: SearchType
  resetDistance?: boolean
  keepSelectedCollection?: boolean
}

export type PerformDynamicSearchFn = (
  options?: PerformDynamicSearchOptions
) => void | Promise<unknown>

export type SearchStore = {
  selectedSpaceType: SearchResultSpaceType
  selectedUseCases: SearchResultUseCase[]
  waitlistedUseCase?: SearchResultUseCase
  searchFilters: SearchFilters
  spaceTypes: SearchResultSpaceType[] | undefined
  useCases: SearchResultUseCase[] | undefined
  spaceTypesLoading: boolean
  useCasesLoading: boolean
  searchType: SearchType | undefined

  showAllInPricing: boolean

  dynamicFilterDefaults: DynamicDefaults | null
  dynamicFilterItems: DynamicFilters | null

  selectedAddress?: Address

  pendingDynamicSearchInput: Partial<DynamicSearchInput> | null
  performSearchDynamic: PerformDynamicSearchFn | null
  updateCategories: UpdateCategoriesFn | null

  // setters
  setDynamicFilterDefaults: (dynamicFilterDefaults: DynamicDefaults) => void
  setDynamicFilterItems: (dynamicFilterItems: DynamicFilters) => void

  setSelectedSpaceType: (selectedCategory: SearchResultSpaceType) => void

  setSelectedUseCases: (selectedCategorys: SearchResultUseCase[]) => void
  toggleUseCase: (useCase: SearchResultUseCase) => void
  resetUseCases: () => void
  getUpdatedUseCases: (useCase: SearchResultUseCase) => SearchResultUseCase[]

  setSearchFilters: (searchFilters: Partial<SearchFilters>) => void
  clearSearchFilters: (exemptions?: Array<keyof SearchFilters>) => void
  setSpaceTypes: (categories: SearchResultSpaceType[]) => void
  setUseCases: (categories: SearchResultUseCase[]) => void
  setSpaceTypesLoading: (spaceTypesLoading: boolean) => void
  setUseCasesLoading: (useCasesLoading: boolean) => void
  setSearchType: (searchType: SearchType | undefined) => void
  setShowAllInPricing: (showAllInPricing: boolean) => void

  setSelectedAddress: (selectedAddress?: Address) => void

  setPendingDynamicSearchInput: (
    pendingDynamicSearchInput: Partial<DynamicSearchInput> | null
  ) => void
  clearPendingDynamicSearchInput: () => void
  setPerformSearchDynamic: (performSearchDynamic: PerformDynamicSearchFn | null) => void
  setUpdateCategories: (updateCategories: UpdateCategoriesFn | null) => void
}

export const useSearchStore = create<SearchStore>((set, get) => {
  const defaultStore: SearchStore = {
    dynamicFilterDefaults: null,
    dynamicFilterItems: null,
    selectedSpaceType: categoriesConstants.spaceTypeAll,
    selectedUseCases: [],
    searchType: undefined,
    searchFilters: {
      ...defaultSearchFilters,
    },
    spaceTypes: undefined,
    useCases: undefined,
    spaceTypesLoading: false,
    useCasesLoading: false,
    showAllInPricing: false,
    pendingDynamicSearchInput: null,
    performSearchDynamic: null,
    updateCategories: null,

    // setters
    setDynamicFilterDefaults: (dynamicFilterDefaults) =>
      set({ dynamicFilterDefaults }),

    setDynamicFilterItems: (dynamicFilterItems) => set({ dynamicFilterItems }),

    setSelectedSpaceType: (selectedCategory) => {
      set({ selectedSpaceType: selectedCategory })
    },

    setSelectedUseCases: (selectedCategorys) => {
      // Only set a selected use case if there is exactly one selected
      // AND that one has waitlistable as true
      const waitlistedUseCase =
        selectedCategorys.length === 1
          ? selectedCategorys.find((x) => x.useCase.waitlistable)
          : undefined
      set({ selectedUseCases: selectedCategorys, waitlistedUseCase })
    },
    getUpdatedUseCases: (useCase) => {
      const { selectedUseCases } = get()
      const newSelectedUseCases = selectedUseCases.map((x) => ({
        ...x,
        useCase: {
          ...x.useCase,
        },
      }))

      const index = newSelectedUseCases.findIndex(
        (x) => x.useCase.slug === useCase.useCase.slug
      )
      if (index > -1) {
        newSelectedUseCases.splice(index, 1)
      } else {
        newSelectedUseCases.push(useCase)
      }

      return newSelectedUseCases
    },
    toggleUseCase: (useCase) => {
      const { getUpdatedUseCases } = get()
      const selectedUseCases = getUpdatedUseCases(useCase)
      const waitlistedUseCase =
        selectedUseCases.length === 1
          ? selectedUseCases.find((x) => x.useCase.waitlistable)
          : undefined
      set({ selectedUseCases, waitlistedUseCase })
    },
    resetUseCases: () => {
      set({ selectedUseCases: [], waitlistedUseCase: undefined })
    },

    setSearchFilters: (updates) => {
      const { searchFilters } = get()

      const { min, max } = IMPERIAL_SYSTEM.distanceFilterBoundaries
      const updatedDistance =
        updates.distance != null
          ? Math.min(max, Math.max(min, updates.distance))
          : searchFilters.distance

      // reset coordinates if the value has changed
      const updatedCoordinates = updates.value
        ? undefined
        : updates.coordinates ?? searchFilters.coordinates

      const updatedSearchFilters = {
        ...searchFilters,
        ...updates,
        distance: updatedDistance,
        coordinates: updatedCoordinates,
        value: updates.value ?? searchFilters.value,
      }

      set({ searchFilters: updatedSearchFilters })
    },

    clearSearchFilters: (
      exemptions = ['value', 'coordinates', 'date', 'start', 'end', 'guests']
    ) => {
      const { searchFilters } = get()
      const retainedSearchFilters =
        exemptions.length === 0
          ? {}
          : Object.keys(searchFilters).reduce((acc, key) => {
              if (!exemptions.includes(key)) return acc
              return {
                ...acc,
                [key]: searchFilters[key],
              }
            }, {} as Partial<SearchFilters>)

      set({
        searchFilters: {
          ...defaultSearchFilters,
          ...retainedSearchFilters,
          dynamicFilters: [],
        },
        spaceTypes: undefined,
        useCases: undefined,
        spaceTypesLoading: false,
        useCasesLoading: false,
      })
    },

    setSpaceTypes: (categories) => set({ spaceTypes: categories }),
    setUseCases: (categories) => set({ useCases: categories }),
    setSpaceTypesLoading: (spaceTypesLoading) => set({ spaceTypesLoading }),
    setUseCasesLoading: (useCasesLoading) => set({ useCasesLoading }),
    setSearchType: (searchType) => set({ searchType }),
    setShowAllInPricing: (showAllInPricing) => set({ showAllInPricing }),
    setSelectedAddress: (selectedAddress) => set({ selectedAddress }),
    setPendingDynamicSearchInput: (pendingDynamicSearchInput) =>
      set({ pendingDynamicSearchInput }),
    clearPendingDynamicSearchInput: () =>
      set({ pendingDynamicSearchInput: null }),
    setPerformSearchDynamic: (performSearchDynamic) =>
      set({ performSearchDynamic }),
    setUpdateCategories: (updateCategories) => set({ updateCategories }),
  }

  return defaultStore
})
